import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

import { HStack, VStack } from "@/components/Layout";
import { Loader } from "@/components/Loading";
import { Breadcrumb } from "@/components/Navigation/Breadcrumb";
import NavigationCard, {
  type NavigationSectionGroup,
} from "@/components/Navigation/NavigationCard";
import { Text } from "@/components/Typography/Text";
import { toast } from "@/components/ui/use-toast";
import { CForm } from "@/features/c-form/CForm";
import { CFormSection } from "@/features/c-form/CFormSection/CFormSection";
import type { ApiError } from "@/lib/api/error";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { AttributeRenderer } from "./profile.$profileId/-components/AttributeRenderer";
import { CertificateSection } from "./profile.$profileId/-components/CertificateSection";
import { EGovAccountSection } from "./profile.$profileId/-components/EGovAccountSection";
import { SECTION_KEYS } from "./profile.$profileId/-constants";
import {
  useGetGovProfileDetail,
  useSaveApplicant,
  useSaveContact,
  useGetCertificateStatus,
  useUploadCertificate,
  useGetOAuthStatus,
  useInitiateOAuth,
} from "./profile.$profileId/-query/hooks";
import {
  GovProfileDetailFormStore,
  useGovProfileDetailFormStore,
} from "./profile.$profileId/-store/store";

export const Route = createFileRoute(
  "/_staff/labor-core/settings/profile/$profileId",
)({
  component: ProfileDetailPage,
});

function ProfileDetailPage() {
  const store = GovProfileDetailFormStore.createStore();
  return (
    <GovProfileDetailFormStore.Provider value={store}>
      <ProfileDetailContent />
    </GovProfileDetailFormStore.Provider>
  );
}

function ProfileDetailContent() {
  const { profileId } = Route.useParams();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.laborCore.settings",
  });
  const { data, isLoading, error } = useGetGovProfileDetail(profileId);
  const { actions } = useGovProfileDetailFormStore();

  const saveApplicantMutation = useSaveApplicant(profileId, {
    onError: (error: ApiError) => {
      const message = actions.onApiError(error);
      if (message) {
        toast({ variant: "destructive", description: message });
      }
    },
  });

  const saveContactMutation = useSaveContact(profileId, {
    onError: (error: ApiError) => {
      const message = actions.onApiError(error);
      if (message) {
        toast({ variant: "destructive", description: message });
      }
    },
  });

  // 証明書ステータス取得
  const { data: certificateData, isLoading: isCertificateLoading } =
    useGetCertificateStatus(profileId);
  const uploadCertificateMutation = useUploadCertificate(profileId);

  // OAuth ステータス取得
  const { data: oauthData, isLoading: isOAuthLoading } =
    useGetOAuthStatus(profileId);
  const initiateOAuthMutation = useInitiateOAuth(profileId);

  // dataが取得されたらストアを初期化
  // actionsはcreateZustandContextで生成されたstableな参照なので依存配列から除外
  useEffect(() => {
    if (data) {
      actions.reset(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // セクションを取得
  const applicantSection = useMemo(
    () =>
      data?.sections.find(
        (section) =>
          String(section.sectionKey) ===
          SECTION_KEYS.APPLICATION_APPLICANT_INFORMATION,
      ),
    [data?.sections],
  );

  const contactSection = useMemo(
    () =>
      data?.sections.find(
        (section) =>
          String(section.sectionKey) ===
          SECTION_KEYS.APPLICATION_CONTACT_INFORMATION,
      ),
    [data?.sections],
  );

  // ローディング状態
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader />
      </div>
    );
  }

  // エラー状態
  if (error) {
    return (
      <div className="p-6">
        <VStack className="gap-4 items-center">
          <Text variant="h3" className="text-error-on-neutral-fill">
            {t("profileDetail.error.loadFailed")}
          </Text>
          <Text variant="p" className="text-neutral-muted-on-fill">
            {error.message || t("profileDetail.error.unknownError")}
          </Text>
        </VStack>
      </div>
    );
  }

  // データがない場合（通常は発生しないが型ガードとして必要）
  if (!data) {
    return (
      <div className="p-6">
        <VStack className="gap-4 items-center">
          <Text variant="p" className="text-neutral-muted-on-fill">
            {t("profileDetail.error.noData")}
          </Text>
        </VStack>
      </div>
    );
  }

  const breadcrumbs = [
    {
      label: t("profileDetail.breadcrumbs.laborCore"),
      link: { to: "/labor-core" as const },
    },
    {
      label: t("profileDetail.breadcrumbs.settings"),
      link: { to: "/labor-core/settings" as const, hash: "applicant-contacts" },
    },
  ];

  // ナビゲーションセクション
  const navigationSections: NavigationSectionGroup[] = [
    {
      titleLabel: "",
      to: "/labor-core/settings/profile/$profileId" as const,
      sections: [
        {
          label: applicantSection
            ? String(applicantSection.name)
            : t("profileDetail.navigation.applicantInformation"),
          hash: "applicant",
        },
        {
          label: contactSection
            ? String(contactSection.name)
            : t("profileDetail.navigation.contactInformation"),
          hash: "contact",
        },
        {
          label: t("profileDetail.navigation.certificate"),
          hash: "certificate",
        },
        {
          label: t("profileDetail.navigation.eGovAccount"),
          hash: "egov-account",
        },
      ],
    },
  ];

  return (
    <VStack className="gap-0">
      {/* ヘッダー */}
      <VStack className="bg-neutral-fill drop-shadow-sm px-8 pt-6 pb-4 gap-2">
        <Breadcrumb breadcrumbs={breadcrumbs} />
        <Text className="pl-2 pt-2" variant="h3">
          {data.profileName}
        </Text>
      </VStack>

      {/* コンテンツ */}
      <HStack className="p-8 gap-8">
        {/* 左側ナビゲーション */}
        <nav className="sticky top-8 h-fit w-60">
          <NavigationCard sections={navigationSections} />
        </nav>

        {/* セクション */}
        <VStack className="w-full gap-4">
          {/* 申請者情報セクション */}
          {applicantSection && (
            <div id="applicant" className="[&_.shadow]:shadow-none [&>div]:p-0">
              <CForm
                editUnit="section"
                itemLayout="card"
                onSave={async () => {
                  const { isValid, errorMessages } = actions.validate(
                    SECTION_KEYS.APPLICATION_APPLICANT_INFORMATION,
                    (field) => t("validation.required", { field }),
                  );
                  if (!isValid) {
                    if (errorMessages.length > 0) {
                      toast({
                        variant: "destructive",
                        description: errorMessages.join("\n"),
                      });
                    }
                    throw new Error("Validation failed");
                  }

                  const updateData = actions.getUpdateRequest();
                  const sectionToSave = updateData.sections.filter(
                    (s) =>
                      String(s.sectionKey) ===
                      SECTION_KEYS.APPLICATION_APPLICANT_INFORMATION,
                  );
                  await saveApplicantMutation.mutateAsync({
                    sections: sectionToSave,
                  });
                }}
                onReset={() => {
                  actions.resetSection(
                    SECTION_KEYS.APPLICATION_APPLICANT_INFORMATION,
                    data,
                  );
                }}
              >
                <CFormSection
                  id="applicant-section"
                  label={String(applicantSection.name)}
                >
                  <AttributeRenderer
                    attributes={applicantSection.attributes}
                    sectionKey={String(applicantSection.sectionKey)}
                  />
                </CFormSection>
              </CForm>
            </div>
          )}

          {/* 連絡先情報セクション */}
          {contactSection && (
            <div id="contact" className="[&_.shadow]:shadow-none [&>div]:p-0">
              <CForm
                editUnit="section"
                itemLayout="card"
                onSave={async () => {
                  const { isValid, errorMessages } = actions.validate(
                    SECTION_KEYS.APPLICATION_CONTACT_INFORMATION,
                    (field) => t("validation.required", { field }),
                  );
                  if (!isValid) {
                    if (errorMessages.length > 0) {
                      toast({
                        variant: "destructive",
                        description: errorMessages.join("\n"),
                      });
                    }
                    throw new Error("Validation failed");
                  }

                  const updateData = actions.getUpdateRequest();
                  const sectionToSave = updateData.sections.filter(
                    (s) =>
                      String(s.sectionKey) ===
                      SECTION_KEYS.APPLICATION_CONTACT_INFORMATION,
                  );
                  await saveContactMutation.mutateAsync({
                    sections: sectionToSave,
                  });
                }}
                onReset={() => {
                  actions.resetSection(
                    SECTION_KEYS.APPLICATION_CONTACT_INFORMATION,
                    data,
                  );
                }}
              >
                <CFormSection
                  id="contact-section"
                  label={String(contactSection.name)}
                >
                  <AttributeRenderer
                    attributes={contactSection.attributes}
                    sectionKey={String(contactSection.sectionKey)}
                  />
                </CFormSection>
              </CForm>
            </div>
          )}

          {/* 電子証明書セクション */}
          <div id="certificate">
            <CertificateSection
              data={certificateData}
              onUpload={(file, pin) => {
                uploadCertificateMutation.mutate({ file, pin });
              }}
              isLoading={
                isCertificateLoading || uploadCertificateMutation.isPending
              }
            />
          </div>

          {/* e-Gov アカウント情報セクション */}
          <div id="egov-account">
            <EGovAccountSection
              data={oauthData}
              onConnect={() => {
                initiateOAuthMutation.mutate();
              }}
              isLoading={isOAuthLoading || initiateOAuthMutation.isPending}
            />
          </div>
        </VStack>
      </HStack>
    </VStack>
  );
}
